import {useCallback, useEffect, useMemo, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table} from "react-bootstrap"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"

type Building = {_id: string; identifier: string; name?: string; campus?: string}
type RoomType = "onDemand" | "pcLab" | "lectureHall" | "seminarRoom"
type Room = {
    _id: string
    building: Building
    identifier: string
    name: string
    type: RoomType
    capacities?: {
        totalSeats: number
        examSeats: {
            oneFreeSeatAndRow: number
            twoFreeSeatsAndOneRow: number
            threeFreeSeatsAndOneRow: number
        }
    }
}

const emptyForm = {
    building: "", identifier: "", name: "", type: "seminarRoom" as RoomType,
    hasCapacities: false, totalSeats: "", oneFreeSeatAndRow: "",
    twoFreeSeatsAndOneRow: "", threeFreeSeatsAndOneRow: "",
}
export default function RoomManagementComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const canManage = auth.hasAnyPermission(["examOfficeEmployee", "examOfficeAssistant"])
    const [rooms, setRooms] = useState<Room[]>([])
    const [buildings, setBuildings] = useState<Building[]>([])
    const [form, setForm] = useState(emptyForm)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [query, setQuery] = useState("")
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    const load = useCallback(async () => {
        if (!canManage) return setLoading(false)
        setLoading(true)
        try {
            const [roomResponse, buildingResponse] = await Promise.all([
                axios.get<Room[]>("/api/rooms", auth.axiosAuthHeader()),
                axios.get<Building[]>("/api/rooms/buildings", auth.axiosAuthHeader()),
            ])
            setRooms(roomResponse.data)
            setBuildings(buildingResponse.data)
            setForm(previous => previous.building || buildingResponse.data.length === 0
                ? previous
                : {...previous, building: buildingResponse.data[0]._id})
            setError(null)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("rooms.messages.loadError")
                : t("rooms.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, canManage, t])

    useEffect(() => {
        void load()
    }, [load])

    const filteredRooms = useMemo(() => {
        const term = query.trim().toLocaleLowerCase()
        if (!term) return rooms
        return rooms.filter(room => [room.identifier, room.name, room.type,
            room.building?.identifier, room.building?.name, room.building?.campus]
            .filter(Boolean).some(value => value!.toLocaleLowerCase().includes(term)))
    }, [query, rooms])

    const resetForm = () => {
        setEditingId(null)
        setForm({...emptyForm, building: buildings[0]?._id ?? ""})
    }

    const edit = (room: Room) => {
        setEditingId(room._id)
        setForm({
            building: room.building._id,
            identifier: room.identifier,
            name: room.name,
            type: room.type,
            hasCapacities: Boolean(room.capacities),
            totalSeats: String(room.capacities?.totalSeats ?? ""),
            oneFreeSeatAndRow: String(room.capacities?.examSeats.oneFreeSeatAndRow ?? ""),
            twoFreeSeatsAndOneRow: String(room.capacities?.examSeats.twoFreeSeatsAndOneRow ?? ""),
            threeFreeSeatsAndOneRow: String(room.capacities?.examSeats.threeFreeSeatsAndOneRow ?? ""),
        })
        window.scrollTo({top: 0, behavior: "smooth"})
    }

    const save = async () => {
        setSaving(true)
        setError(null)
        setSuccess(null)
        try {
            const {
                hasCapacities, totalSeats, oneFreeSeatAndRow,
                twoFreeSeatsAndOneRow, threeFreeSeatsAndOneRow, ...roomFields
            } = form
            const payload = {
                ...roomFields,
                capacities: hasCapacities
                    ? {
                        totalSeats: Number(totalSeats),
                        examSeats: {
                            oneFreeSeatAndRow: Number(oneFreeSeatAndRow),
                            twoFreeSeatsAndOneRow: Number(twoFreeSeatsAndOneRow),
                            threeFreeSeatsAndOneRow: Number(threeFreeSeatsAndOneRow),
                        },
                    }
                    : null,
            }
            if (editingId) {
                await axios.put(`/api/rooms/${editingId}`, payload, auth.axiosAuthHeader())
            } else {
                await axios.post("/api/rooms", payload, auth.axiosAuthHeader())
            }
            setSuccess(t(editingId ? "rooms.messages.updated" : "rooms.messages.created"))
            resetForm()
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("rooms.messages.saveError")
                : t("rooms.messages.saveError"))
        } finally {
            setSaving(false)
        }
    }

    if (!canManage) return <div className="component"><Alert variant="danger">
        {t("rooms.messages.forbidden")}
    </Alert></div>

    return <div className="component">
        <Row className="g-3">
            <Col xs={12} xl={4}>
                <Card>
                    <Card.Header><h3 className="mb-0"><i className="bi bi-door-open me-2"/>
                        {t(editingId ? "rooms.edit" : "rooms.create")}</h3></Card.Header>
                    <Card.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>{t("rooms.fields.building")}</Form.Label>
                            <Form.Select value={form.building}
                                onChange={event => setForm({...form, building: event.target.value})}>
                                {buildings.map(building => <option key={building._id} value={building._id}>
                                    {building.identifier} – {building.name || building.campus}
                                </option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("rooms.fields.identifier")}</Form.Label>
                            <Form.Control value={form.identifier}
                                onChange={event => setForm({...form, identifier: event.target.value})}/>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("rooms.fields.name")}</Form.Label>
                            <Form.Control value={form.name}
                                onChange={event => setForm({...form, name: event.target.value})}/>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("rooms.fields.type")}</Form.Label>
                            <Form.Select value={form.type}
                                onChange={event => setForm({...form, type: event.target.value as RoomType})}>
                                {(["onDemand", "pcLab", "lectureHall", "seminarRoom"] as RoomType[]).map(value =>
                                    <option key={value} value={value}>{t(`rooms.types.${value}`)}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Check className="mb-3" type="switch"
                            label={t("rooms.capacities.enabled")}
                            checked={form.hasCapacities}
                            onChange={event => setForm({...form, hasCapacities: event.target.checked})}/>
                        {form.hasCapacities && <Card className="mb-3">
                            <Card.Body>
                                <Row className="g-3">
                                    {([
                                        ["totalSeats", "totalSeats"],
                                        ["oneFreeSeatAndRow", "oneFreeSeatAndRow"],
                                        ["twoFreeSeatsAndOneRow", "twoFreeSeatsAndOneRow"],
                                        ["threeFreeSeatsAndOneRow", "threeFreeSeatsAndOneRow"],
                                    ] as const).map(([field, label]) =>
                                        <Col xs={12} md={6} key={field}>
                                            <Form.Label>{t(`rooms.capacities.${label}`)}</Form.Label>
                                            <Form.Control type="number" min={0} step={1}
                                                value={form[field]}
                                                onChange={event => setForm({...form, [field]: event.target.value})}/>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>}
                        <div className="d-flex gap-2">
                            <Button onClick={save} disabled={saving || !form.building ||
                                !form.identifier.trim() || !form.name.trim() ||
                                (form.hasCapacities && [
                                    form.totalSeats, form.oneFreeSeatAndRow,
                                    form.twoFreeSeatsAndOneRow, form.threeFreeSeatsAndOneRow,
                                ].some(value => value === "" || Number(value) < 0))}>
                                {saving && <Spinner size="sm" className="me-2"/>}
                                {t(editingId ? "rooms.saveChanges" : "rooms.create")}
                            </Button>
                            {editingId && <Button variant="secondary" onClick={resetForm}>{t("cancel", {ns: "common"})}</Button>}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col xs={12} xl={8}>
                <Card>
                    <Card.Header><h3 className="mb-0">{t("rooms.overview")}</h3></Card.Header>
                    <Card.Body>
                        <Form.Control type="search" className="mb-3" value={query}
                            placeholder={t("rooms.search")}
                            onChange={event => setQuery(event.target.value)}/>
                        {loading ? <div><Spinner size="sm" className="me-2"/>{t("rooms.loading")}</div> :
                            <Table responsive hover>
                                <thead><tr><th>{t("rooms.fields.building")}</th><th>{t("rooms.fields.room")}</th><th>{t("rooms.fields.name")}</th><th>{t("rooms.fields.type")}</th><th>{t("rooms.capacities.short")}</th><th/></tr></thead>
                                <tbody>
                                {filteredRooms.map(room => <tr key={room._id}>
                                    <td><Badge bg="secondary">{room.building?.identifier}</Badge><br/>
                                        <small>{room.building?.name}</small></td>
                                    <td><strong>{room.identifier}</strong></td>
                                    <td>{room.name}</td>
                                    <td>{t(`rooms.types.${room.type}`)}</td>
                                    <td>{room.capacities
                                        ? `${room.capacities.totalSeats} / ${room.capacities.examSeats.oneFreeSeatAndRow} / ${room.capacities.examSeats.twoFreeSeatsAndOneRow} / ${room.capacities.examSeats.threeFreeSeatsAndOneRow}`
                                        : "–"}</td>
                                    <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => edit(room)}>
                                        <i className="bi bi-pencil me-1"/>{t("rooms.editAction")}
                                    </Button></td>
                                </tr>)}
                                {filteredRooms.length === 0 && <tr><td colSpan={6} className="text-muted">{t("rooms.empty")}</td></tr>}
                                </tbody>
                            </Table>}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </div>
}
